import React, { useState, useRef, useEffect } from 'react';
import { useIdioma } from '../../i18n/idioma';
import { ChevronDown } from 'lucide-react';
import { IconoBotRaza } from '../ui/IconoBotRaza';
import { useAIChat, OpenContextualChatOptions } from '../../contexts/AIChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { canUseAI } from '../../constants/roles';

export interface AskRazaPrompt {
  label: string;
  prompt: string;
  autoSend?: boolean;
}

interface AskRazaButtonProps {
  contextLabel: string;
  contextualFocus: string;
  prompts?: AskRazaPrompt[];
  /** Shown when no prompt menu — opens empty chat with context */
  defaultPrompt?: string;
  variant?: 'compact' | 'pill' | 'card';
  className?: string;
}

const AskRazaButton: React.FC<AskRazaButtonProps> = ({
  contextLabel,
  contextualFocus,
  prompts = [],
  defaultPrompt,
  variant = 'compact',
  className = '',
}) => {
    const { t } = useIdioma();
  const { openContextualChat } = useAIChat();
  const { userRole } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const launch = (opts: Partial<OpenContextualChatOptions>) => {
    setMenuOpen(false);
    openContextualChat({
      contextLabel,
      contextualFocus,
      newConversation: true,
      ...opts,
    });
  };

  const hasMenu = prompts.length > 0;

  // Field roles have no AI access, and the chat panel is not mounted for them —
  // rendering this would leave a button that visibly does nothing when tapped.
  // Placed after all hooks so the hook order stays identical across renders.
  if (!canUseAI(userRole)) return null;

  const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 active:scale-[0.98]';

  const baseClass =
    variant === 'pill'
      ? `inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl text-xs font-semibold bg-navy-850 text-brand-400 hover:bg-navy-700 transition-colors duration-150 ${focusRing}`
      : variant === 'card'
        ? `w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-navy-850 text-white hover:bg-navy-700 transition-colors duration-150 text-left font-semibold text-sm ${focusRing}`
        : `inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl text-xs font-semibold bg-brand-50 text-navy-950 hover:bg-brand-100 border border-brand-300 transition-colors duration-150 ${focusRing}`;

  const handlePrimaryClick = () => {
    if (hasMenu) {
      setMenuOpen(v => !v);
      return;
    }
    launch({
      prompt: defaultPrompt,
      autoSend: !!defaultPrompt,
    });
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        type="button"
        onClick={handlePrimaryClick}
        className={baseClass}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-expanded={hasMenu ? menuOpen : undefined}
      >
        {/* Carita estática y no el Lottie: dos de las tres variantes de este
            botón son `bg-navy-850`, y el robot del archivo es oscuro (su disco
            amarillo se lo quitaron porque lo pone el botón flotante). Sobre
            navy sería una mancha invisible; esto hereda `currentColor`. */}
        <IconoBotRaza size={variant === 'compact' ? 14 : 16} className="shrink-0" />
        <span>{variant === 'card' ? t('ai.askraza.botontarjeta') : t('ai.askraza.boton')}</span>
        {hasMenu && (
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {menuOpen && hasMenu && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-acero-200 rounded-2xl shadow-s3 z-[300] py-1 overflow-hidden">
          <p className="px-3 py-2 label-ui border-b border-acero-100">
            {contextLabel}
          </p>
          {prompts.map(p => (
            <button
              key={p.label}
              type="button"
              onClick={() => launch({ prompt: p.prompt, autoSend: p.autoSend ?? true })}
              className="w-full text-left px-3 py-2.5 text-sm font-medium text-navy-950 hover:bg-brand-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset"
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => launch({})}
            className="w-full text-left px-3 py-2 text-xs text-acero-500 hover:bg-acero-50 hover:text-navy-950 border-t border-acero-100 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {t('ai.askraza.chatvacio')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AskRazaButton;
